import { getIn, getName } from 'utils';


const REQUIRED_FIELDS = [
  'streetNumber',
  'streetName',
  'municipality',
  'stateCode',
  'postalCode',
  'countryCode',
  'countryName',
];

// stateCode is intentionally omitted here: when a country form is active the
// state/province field is validated separately below (US → stateCode,
// non-US → province), so including it in the base list would double-count it
// for US addresses and wrongly require it for non-US ones.
const REQUIRED_FIELDS_FOR_COUNTRY_FORM = [
  'streetNumber',
  'streetName',
  'municipality',
  'postalCode',
  'countryCode',
];

/**
 * Returns `true` when the address at `namespace` has any required-field
 * errors or missing values.
 *
 * @param {string}  namespace  - Final Form field prefix (e.g. "property.address")
 * @param {Object}  errors     - Final Form `errors` object
 * @param {Object}  values     - Final Form `values` object
 * @param {boolean} [isRequired=true] - Whether the address block is required at all
 * @param {string}  [countryCode]     - Resolved country code (e.g. "US"); when
 *                                      provided the country-aware field list is used
 */
export const hasRequiredFieldErrors = (namespace, errors, values, isRequired = true, countryCode) => {
  if (!isRequired) return false;

  // When both objects are falsy the form hasn't been initialised yet,
  // so we conservatively treat the address as invalid.
  if (!errors && !values) return true;

  const requiredFields = countryCode ? REQUIRED_FIELDS_FOR_COUNTRY_FORM : REQUIRED_FIELDS;

  const hasBaseErrors = requiredFields.some((fieldName) => {
    const fullFieldName = getName(namespace, fieldName);
    const fieldError = getIn(fullFieldName, errors || {});
    const fieldValue = getIn(fullFieldName, values || {});

    return fieldError || !fieldValue;
  });

  if (hasBaseErrors) return true;

  if (countryCode === 'US') {
    const stateFieldName = getName(namespace, 'stateCode');
    const stateError = getIn(stateFieldName, errors || {});
    const stateValue = getIn(stateFieldName, values || {});
    return stateError || !stateValue;
  }

  if (countryCode) {
    const provinceFieldName = getName(namespace, 'province');
    const provinceError = getIn(provinceFieldName, errors || {});
    const provinceValue = getIn(provinceFieldName, values || {});
    return provinceError || !provinceValue;
  }

  return false;
};

/**
 * Resolves the country code from form values and delegates to
 * `hasRequiredFieldErrors`. This is the shared logic used by both
 * `AutoExpandOnError` and `HideComponentsButton`.
 *
 * @param {string}  namespace             - Final Form field prefix
 * @param {Object}  errors                - Final Form `errors` object
 * @param {Object}  values                - Final Form `values` object
 * @param {boolean} [isRequired=true]     - Whether the address block is required
 * @param {string}  [countryCodeFieldName] - Full field name for the country code
 */
export const checkAddressErrors = (namespace, errors, values, isRequired = true, countryCodeFieldName) => {
  const countryCodeValue = countryCodeFieldName
    ? getIn(countryCodeFieldName, values || {})
    : undefined;

  return countryCodeValue !== undefined
    ? hasRequiredFieldErrors(namespace, errors, values, isRequired, countryCodeValue)
    : hasRequiredFieldErrors(namespace, errors, values, isRequired);
};
